import type { FixtureBox } from "@rooms/roomsClient";
import { cn } from "@scribenote/brian-design-system";
import { type PointerEvent, useRef, useState } from "react";

import {
  fromDrag,
  type Point,
  type SourceSize,
  toDisplay,
  toSource,
} from "./fixtureBoxes";

interface OverlayBox {
  key: string;
  box: FixtureBox;
  label: string;
}

const TONES = {
  area: {
    rect: "fill-fill-brian stroke-ink-brian",
    opacity: 0.2,
    label: "text-ink-brian",
  },
  mask: {
    rect: "fill-fill-declined stroke-ink-declined",
    opacity: 0.35,
    label: "font-mono text-ink-declined",
  },
  door: {
    rect: "fill-accent stroke-accent",
    opacity: 0.2,
    label: "text-accent",
  },
} as const;

/**
 * Boxes over a still, in its source pixels; a drag draws a new one. `draft` is a
 * box drawn but not yet kept, shown dashed.
 */
export const BoxOverlay = ({
  size,
  boxes,
  tone,
  label,
  draft = null,
  disabled = false,
  onDraw,
}: {
  size: SourceSize;
  boxes: readonly OverlayBox[];
  tone: keyof typeof TONES;
  label: string;
  draft?: FixtureBox | null;
  disabled?: boolean;
  onDraw: (box: FixtureBox) => void;
}) => {
  const [drag, setDrag] = useState<{ start: Point; end: Point } | null>(null);
  const svg = useRef<SVGSVGElement>(null);
  const style = TONES[tone];

  const at = (event: PointerEvent): Point | null =>
    svg.current
      ? toSource(
          { x: event.clientX, y: event.clientY },
          svg.current.getBoundingClientRect(),
          size,
        )
      : null;
  const preview = (drag && fromDrag(drag.start, drag.end, size)) ?? draft;

  return (
    <>
      <svg
        ref={svg}
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        role="group"
        aria-label={label}
        className={cn(
          "absolute inset-0 h-full w-full touch-none select-none",
          disabled ? "cursor-not-allowed" : "cursor-crosshair",
        )}
        onPointerDown={(event) => {
          const start = event.button === 0 && !disabled ? at(event) : null;
          if (!start) return;
          event.currentTarget.setPointerCapture(event.pointerId);
          setDrag({ start, end: start });
        }}
        onPointerMove={(event) => {
          const end = drag && at(event);
          if (end) setDrag({ start: drag.start, end });
        }}
        onPointerUp={(event) => {
          const end = drag && at(event);
          setDrag(null);
          const box = end && fromDrag(drag.start, end, size);
          if (box) onDraw(box);
        }}
        onPointerCancel={() => setDrag(null)}
      >
        {boxes.map(({ key, box }) => (
          <rect
            key={key}
            {...toDisplay(box, size)}
            vectorEffect="non-scaling-stroke"
            strokeWidth={2}
            fillOpacity={style.opacity}
            className={cn("pointer-events-none", style.rect)}
          />
        ))}
        {preview && (
          <rect
            {...toDisplay(preview, size)}
            fill="none"
            vectorEffect="non-scaling-stroke"
            strokeWidth={2}
            strokeDasharray="4 3"
            className={cn(
              "pointer-events-none",
              tone === "area" ? "stroke-ink-brian" : "stroke-accent",
            )}
          />
        )}
      </svg>
      {boxes.map(({ key, box, label: text }) => (
        <span
          key={key}
          className={cn(
            "pointer-events-none absolute rounded-paper bg-card px-1 text-fine-print",
            style.label,
          )}
          style={{
            left: `${(box[0] / size.width) * 100}%`,
            top: `${(box[1] / size.height) * 100}%`,
          }}
        >
          {text}
        </span>
      ))}
    </>
  );
};
